import { z } from 'zod';
import { MovableDocumentCategorySchema, PartieAdverseSubcategorySchema } from '../documents/moveTargets.js';
import { GmailDraftAttachmentReadParamsSchema } from './gmailDraft.js';

const FileNameSchema = z.string().trim().min(1).max(200).regex(/^[^\x00-\x1f\x7f/\\]+$/);
const PartIdSchema = GmailDraftAttachmentReadParamsSchema.shape.partId;

// Hector product budgets, not Google service deadlines. Queueing, source checks and
// storage share one operation deadline across every HTTP wave; the host receipt
// gets a small transport margin.
export const GMAIL_SERVER_FILE_OPERATION_TIMEOUT_MS = 5 * 60_000;
export const GMAIL_HOST_FILE_ACTION_TIMEOUT_MS = GMAIL_SERVER_FILE_OPERATION_TIMEOUT_MS + 30_000;

/** References select Google files; this contract never carries their bytes or OAuth tokens. */
export const GmailFileImportRequestSchema = z.object({
  caseId: z.string().uuid(),
  category: MovableDocumentCategorySchema,
  subcategory: PartieAdverseSubcategorySchema.nullish(),
  folderPath: z.array(z.string().min(1).max(250)).max(64).optional(),
  includeMessage: z.boolean(),
  attachmentIds: z.array(PartIdSchema).max(100).default([]),
  messageFileName: FileNameSchema.optional(),
  attachmentNames: z.record(PartIdSchema, FileNameSchema).optional(),
}).strict().superRefine((input, ctx) => {
  if (!input.includeMessage && input.attachmentIds.length === 0) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['attachmentIds'], message: 'Choisissez au moins un courrier ou une pièce jointe à classer.' });
  }
  if (new Set(input.attachmentIds).size !== input.attachmentIds.length) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['attachmentIds'], message: 'Chaque pièce jointe Gmail ne peut figurer qu’une fois dans ce classement.' });
  }
  if (Object.keys(input.attachmentNames ?? {}).some((id) => !input.attachmentIds.includes(id))) {
    ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['attachmentNames'], message: 'Un renommage doit viser une pièce jointe sélectionnée.' });
  }
});

export const GmailFileImportResponseSchema = z.object({
  files: z.array(z.object({
    fileName: z.string(), success: z.boolean(), documentId: z.string().uuid().optional(), error: z.string().optional(),
  }).strict()),
  synthesisHeld: z.boolean(),
  filingMarked: z.boolean().optional(),
}).strict();

export const GmailAttachmentReadBodySchema = z.object({ conversationId: z.string().uuid().optional() }).strict();
/** The same temporary-file reference as the multipart chat upload, without the browser round trip. */
export const GmailAttachmentReadResponseSchema = z.object({
  temp_file_id: z.string().uuid(), original_filename: z.string(), mime_type: z.string(), size_bytes: z.number().int().nonnegative(),
}).strict();

export type GmailFileImportRequest = z.infer<typeof GmailFileImportRequestSchema>;
export type GmailFileImportResponse = z.infer<typeof GmailFileImportResponseSchema>;
